import axios from 'axios';

const STATE_ADVISORY_BASE_URL =
  'https://stategovtraveladvisory-production.up.railway.app/api';

// Server-side proxy via Netlify Function (replaces third-party CORS proxies)
const fetchWithProxy = async (url) => {
  try {
    const proxyUrl = `/api/travel-advisory-proxy?url=${encodeURIComponent(url)}`;
    const response = await axios.get(proxyUrl, {
      timeout: 15000,
    });
    return response.data;
  } catch (error) {
    console.error('Travel advisory proxy failed:', error.message);
    throw new Error('Travel advisory proxy failed');
  }
};

// US State Department Travel Advisory API service
export const stateTravelAdvisoryApi = {
  // Get all travel advisories
  getAllAdvisories: async (page = 1, limit = 100) => {
    try {
      const url = `${STATE_ADVISORY_BASE_URL}/advisories`;
      // Try direct fetch first
      try {
        const directResponse = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'application/json',
          },
        });
        if (directResponse.ok) {
          return await directResponse.json();
        }
      } catch (directError) {
        console.log('Direct fetch failed, using CORS proxy...');
      }

      // Use CORS proxy if direct fetch fails
      return await fetchWithProxy(url);
    } catch (error) {
      console.error('Error fetching State Department advisories:', error);
      return null;
    }
  },

  // Get advisory for a specific country
  getCountryAdvisory: async (countryCode) => {
    try {
      const url = `${STATE_ADVISORY_BASE_URL}/advisories?countryCode=${countryCode}`;
      // Try direct fetch first
      try {
        const directResponse = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'application/json',
          },
        });
        if (directResponse.ok) {
          return await directResponse.json();
        }
      } catch (directError) {
        console.log('Direct fetch failed, using CORS proxy...');
      }

      // Use CORS proxy if direct fetch fails
      return await fetchWithProxy(url);
    } catch (error) {
      console.error(`Error fetching advisory for ${countryCode}:`, error);
      return null;
    }
  },
};

// Helper function to map State Department level to our severity
export const mapStateAdvisoryLevel = (levelNumber) => {
  // Level 1: Exercise Normal Precautions = Low
  // Level 2: Exercise Increased Caution = Medium
  // Level 3: Reconsider Travel = High
  // Level 4: Do Not Travel = High (Critical)
  if (levelNumber === 4) {
    return 'High';
  } else if (levelNumber === 3) {
    return 'Reconsider';
  } else if (levelNumber === 2) {
    return 'Medium';
  } else if (levelNumber === 1) {
    return 'Low';
  }
  return 'Low'; // Default
};

// Helper function to map State Department level to risk level for map coloring
export const mapStateAdvisoryToRiskLevel = (levelNumber) => {
  if (levelNumber === 4) {
    return 'high'; // Do Not Travel - Red
  } else if (levelNumber === 3) {
    return 'reconsider'; // Reconsider Travel - Orange
  } else if (levelNumber === 2) {
    return 'medium'; // Exercise Increased Caution - Yellow
  } else if (levelNumber === 1) {
    return 'low'; // Exercise Normal Precautions - Teal/Green
  }
  return 'default'; // Default gray
};

// Helper function to get country code from country name
export const getCountryCodeFromName = (countryName) => {
  const nameToCodeMap = {
    'United States': 'US',
    'United States of America': 'US',
    France: 'FR',
    Canada: 'CA',
    'United Kingdom': 'GB',
    Germany: 'DE',
    Japan: 'JP',
    Australia: 'AU',
    China: 'CN',
    Brazil: 'BR',
    Russia: 'RU',
    'Russian Federation': 'RU',
    India: 'IN',
    Mexico: 'MX',
    Spain: 'ES',
    Italy: 'IT',
    Egypt: 'EG',
    'South Africa': 'ZA',
    Thailand: 'TH',
    Singapore: 'SG',
    Switzerland: 'CH',
    Netherlands: 'NL',
    Belgium: 'BE',
    Sweden: 'SE',
    Norway: 'NO',
    Denmark: 'DK',
    Finland: 'FI',
    Poland: 'PL',
    Greece: 'GR',
    Portugal: 'PT',
    Turkey: 'TR',
    'Saudi Arabia': 'SA',
    'United Arab Emirates': 'AE',
    'South Korea': 'KR',
    'New Zealand': 'NZ',
    Argentina: 'AR',
    Chile: 'CL',
    Colombia: 'CO',
    Peru: 'PE',
    Venezuela: 'VE',
    Philippines: 'PH',
    Vietnam: 'VN',
    Indonesia: 'ID',
    Malaysia: 'MY',
    Pakistan: 'PK',
    Bangladesh: 'BD',
    Nigeria: 'NG',
    Kenya: 'KE',
    Morocco: 'MA',
    Algeria: 'DZ',
    Tunisia: 'TN',
    Jordan: 'JO',
    Israel: 'IL',
    Lebanon: 'LB',
    Iraq: 'IQ',
    Iran: 'IR',
    Afghanistan: 'AF',
    Ukraine: 'UA',
    Belarus: 'BY',
    Jamaica: 'JM',
    Uzbekistan: 'UZ',
    'Central African Republic': 'CF',
    Mali: 'ML',
    Czechia: 'CZ',
    Benin: 'BJ',
    Grenada: 'GD',
    Nepal: 'NP',
    Armenia: 'AM',
    'Democratic Republic of the Congo': 'CD',
    'Sint Maarten': 'SX',
    'The Gambia': 'GM',
    Comoros: 'KM',
    'Sri Lanka': 'LK',
    Ecuador: 'EC',
    Bulgaria: 'BG',
    Croatia: 'HR',
    'Sao Tome and Principe': 'ST',
    'Equatorial Guinea': 'GQ',
    Maldives: 'MV',
    Antarctica: 'AQ',
    'Sierra Leone': 'SL',
  };
  return nameToCodeMap[countryName] || null;
};
